import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { API } from "@/App";
import { toast } from "sonner";
import { User, Mail, FolderOpen, Link, FileText, Send, Loader2 } from "lucide-react";
import axios from "axios";

export const VideoUploadForm = ({ onSuccess }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    project_name: "",
    google_drive_link: "",
    notes: ""
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }
    
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }
    
    if (!formData.project_name.trim()) {
      newErrors.project_name = "Project name is required";
    }
    
    if (!formData.google_drive_link.trim()) {
      newErrors.google_drive_link = "Google Drive link is required";
    } else if (!formData.google_drive_link.includes("drive.google.com") && !formData.google_drive_link.includes("docs.google.com")) {
      newErrors.google_drive_link = "Please enter a valid Google Drive link";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error("Please fix the errors in the form");
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/webhook/video-upload`, formData);

      if (response.data.success) {
        toast.success("Video submitted successfully!");
        onSuccess({
          ...formData,
          submissionId: response.data.submission_id
        });
      }
    } catch (error) {
      console.error("Submission error:", error);
      toast.error(error.response?.data?.detail || "Failed to submit. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card id="upload-form" className="opacity-0 animate-scale-in border-border/60 shadow-xl bg-card/80 backdrop-blur-sm" style={{ animationDelay: '0.2s' }}>
      <CardHeader className="text-center pb-6">
        <div className="mx-auto mb-4 p-3 rounded-xl bg-primary/10 w-fit">
          <Send className="h-6 w-6 text-primary" />
        </div>
        <CardTitle className="font-heading text-2xl text-foreground">Submit Your Video</CardTitle>
        <CardDescription className="text-muted-foreground">
          Fill in the details below to submit your video for processing
        </CardDescription>
      </CardHeader>
      
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Name Field */}
          <FormField
            icon={<User className="h-4 w-4" />}
            label="Name"
            name="name"
            type="text"
            placeholder="Your full name"
            value={formData.name}
            onChange={handleInputChange}
            error={errors.name}
            required
          />

          {/* Email Field */}
          <FormField
            icon={<Mail className="h-4 w-4" />}
            label="Email"
            name="email"
            type="email"
            placeholder="your@email.com"
            value={formData.email}
            onChange={handleInputChange}
            error={errors.email}
            required
          />

          {/* Project Name Field */}
          <FormField
            icon={<FolderOpen className="h-4 w-4" />}
            label="Project / Label"
            name="project_name"
            type="text"
            placeholder="e.g., Summer Campaign 2024"
            value={formData.project_name}
            onChange={handleInputChange}
            error={errors.project_name}
            required
          />

          {/* Google Drive Link Field */}
          <FormField
            icon={<Link className="h-4 w-4" />}
            label="Google Drive Link"
            name="google_drive_link"
            type="url"
            placeholder="https://drive.google.com/..."
            value={formData.google_drive_link}
            onChange={handleInputChange}
            error={errors.google_drive_link}
            required
          />

          {/* Notes Field */}
          <div className="space-y-2">
            <Label htmlFor="notes" className="flex items-center gap-2 text-foreground font-medium">
              <FileText className="h-4 w-4 text-muted-foreground" />
              Notes / Instructions
              <span className="text-muted-foreground font-normal text-xs">(optional)</span>
            </Label>
            <Textarea
              id="notes"
              name="notes"
              placeholder="Any specific instructions or notes for processing..."
              value={formData.notes}
              onChange={handleInputChange}
              className="min-h-[100px] resize-none bg-background border-input focus:border-primary focus:ring-primary/20 transition-all duration-200"
            />
          </div>

          {/* Submit Button */}
          <Button 
            type="submit" 
            size="lg" 
            className="w-full h-12 text-base font-semibold bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg hover:shadow-xl transition-all duration-300"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <Send className="mr-2 h-5 w-5" />
                Submit Video
              </>
            )}
          </Button>

          {/* Helper Text */}
          <p className="text-center text-xs text-muted-foreground">
            By submitting, you agree that your video will be processed according to our service terms.
          </p>
        </form>
      </CardContent>
    </Card>
  );
};

const FormField = ({ icon, label, name, type, placeholder, value, onChange, error, required }) => (
  <div className="space-y-2">
    <Label htmlFor={name} className="flex items-center gap-2 text-foreground font-medium">
      <span className="text-muted-foreground">{icon}</span>
      {label}
      {required && <span className="text-destructive">*</span>}
    </Label>
    <div className="relative">
      <Input
        id={name}
        name={name}
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        className={`bg-background border-input focus:border-primary focus:ring-primary/20 transition-all duration-200 ${
          error ? "border-destructive focus:border-destructive" : ""
        }`}
        required={required}
      />
    </div>
    {error && (
      <p className="text-xs text-destructive flex items-center gap-1">
        <span className="inline-block w-1 h-1 rounded-full bg-destructive" />
        {error}
      </p>
    )}
  </div>
);
